import { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Download } from "lucide-react";
import { piece } from "@/data/piece";
import { useLang, usePageMeta } from "@/App";
import { Reveal } from "@/components/Section";
import AudioPlaylist from "@/components/AudioPlaylist";
import { tracks } from "@/data/audioTracks";

const LABELS = {
  fr: { kicker: "Lecture en ligne — consultation intégrale", note: "Texte intégral en français", download: "Télécharger le PDF", order: "Commander l'édition papier — 20 €", listen: "Écouter la pièce — lecture intégrale", scene: "Acte I, scène 1 — le monologue de Petit-Jean", voiceNote: "Lecture IA française — voix ElevenLabs" },
  en: { kicker: "Online reading — full text", note: "Full text in French", download: "Download the PDF", order: "Order the print edition — €20", listen: "Listen to the play — full reading", scene: "Act I, scene 1 — Petit-Jean's monologue", voiceNote: "French AI voice — ElevenLabs" },
  es: { kicker: "Lectura en línea — texto íntegro", note: "Texto íntegro en francés", download: "Descargar el PDF", order: "Encargar la edición impresa — 20 €", listen: "Escuchar la pieza — lectura íntegra", scene: "Acto I, escena 1 — monólogo de Petit-Jean", voiceNote: "Voz francesa IA — ElevenLabs" },
  de: { kicker: "Online-Lektüre — vollständiger Text", note: "Vollständiger Text auf Französisch", download: "PDF herunterladen", order: "Druckausgabe bestellen — 20 €", listen: "Das Stück anhören — vollständige Lesung", scene: "Akt I, Szene 1 — Petit-Jeans Monolog", voiceNote: "Französische KI-Stimme — ElevenLabs" },
};

const LecturePage = () => {
  const { lang } = useLang();
  const L = LABELS[lang] || LABELS.fr;
  usePageMeta(
    "Le Crapaud et le Berger — lecture en ligne | Gilles de Chassey",
    "Lisez l'intégralité de la farce en trois actes et en vers de Gilles de Chassey — Les Nouveaux Plaideurs — avec la lecture audio complète, scène par scène. Une production VIVA ARTIST.",
    "/lecture"
  );

  const acts = useMemo(() => {
    let n = 0;
    return piece.blocks.map((b) => (b.type === "act" ? ++n : null));
  }, []);

  return (
    <div className="min-h-screen bg-paper" data-testid="lecture-page">
      <div className="pt-28 md:pt-36 px-5 md:px-10 pb-24">
        <div className="max-w-[1100px] mx-auto">
          <Reveal>
            <Link to="/" data-testid="lecture-back-link" className="inline-flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.25em] text-muted2 hover:text-terra transition-colors mb-14">
              <ArrowLeft size={14} /> Gilles de Buretel de Chassey
            </Link>
          </Reveal>

          <div className="grid md:grid-cols-[220px_1fr] gap-10 items-start mb-16">
            <Reveal delay={0.05}>
              <div className="relative">
                <div className="absolute -inset-2 border border-gold/40 translate-x-3 translate-y-3" />
                <img src="/images/couverture-crapaud-berger.png" alt="Couverture — Le Crapaud et le Berger" className="relative w-full border border-line shadow-[0_24px_60px_-24px_rgba(33,26,19,0.45)]" />
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="font-mono2 text-[10px] md:text-[11px] uppercase tracking-[0.3em] text-terra mb-4">{L.kicker}</p>
              <h1 data-testid="lecture-title" className="font-display font-light text-5xl sm:text-6xl text-ink leading-[1.02] tracking-tight">
                {piece.meta.title}
              </h1>
              <p className="font-display italic text-2xl text-bronze mt-3">{piece.meta.subtitle}</p>
              <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-muted2 mt-4">{piece.meta.badge} · {piece.meta.author}</p>
              <p className="text-sm text-ink2 mt-4 max-w-xl leading-relaxed">{piece.meta.credit}</p>
              <p className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-muted2 mt-3">{piece.meta.edition} · {L.note}</p>
              <div className="flex flex-wrap gap-4 mt-8">
                <a
                  data-testid="lecture-download-pdf"
                  href="/le-crapaud-et-le-berger.pdf"
                  target="_blank"
                  rel="noreferrer"
                  className="group inline-flex items-center gap-2.5 border border-ink text-ink hover:bg-ink hover:text-paper transition-colors px-6 py-3 font-mono2 text-[10px] uppercase tracking-[0.2em]"
                >
                  <Download size={13} /> {L.download}
                </a>
                <Link
                  data-testid="lecture-order-link"
                  to="/#contact"
                  className="inline-flex items-center gap-2.5 bg-terra text-paper hover:bg-terraBright transition-colors px-6 py-3 font-mono2 text-[10px] uppercase tracking-[0.2em]"
                >
                  {L.order}
                </Link>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15} className="mb-14">
            <AudioPlaylist tracks={tracks} listen={L.listen} voiceNote={L.voiceNote} />
          </Reveal>

          <nav data-testid="lecture-act-nav" className="flex gap-3 mb-14 sticky top-[76px] z-40 bg-paper/90 backdrop-blur-md py-3 border-y border-line">
            {["ACTE PREMIER", "ACTE II", "ACTE III"].map((a, i) => (
              <a
                key={a}
                data-testid={`lecture-nav-acte-${i + 1}`}
                href={`#acte-${i + 1}`}
                className="font-mono2 text-[10px] uppercase tracking-[0.2em] px-4 py-2 border border-line text-ink2 hover:border-terra hover:text-terra transition-colors"
              >
                {a}
              </a>
            ))}
          </nav>

          <div data-testid="lecture-text" className="pb-10">
            {piece.blocks.map((b, i) => {
              if (b.type === "act") {
                return (
                  <h2 key={i} id={`acte-${acts[i]}`} data-testid={`lecture-act-${acts[i]}`} className="scroll-mt-36 font-display font-light text-4xl md:text-5xl text-ink mt-20 mb-4 pb-6 border-b border-line first:mt-0">
                    <span className="text-terra italic">{b.text}</span>
                  </h2>
                );
              }
              if (b.type === "scene") {
                return (
                  <p key={i} className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-terra mt-12 mb-6">
                    — {b.text} —
                  </p>
                );
              }
              if (b.type === "dir") {
                return <p key={i} className="font-display italic text-lg text-muted2 my-3 pl-4">{b.text}</p>;
              }
              if (b.type === "speech") {
                return (
                  <div key={i} className="my-7">
                    <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-bronze mb-2">{b.speaker}</p>
                    <div className="pl-4 md:pl-8 border-l border-line">
                      {b.lines.map((l, j) => (
                        <p key={j} className="font-display text-xl md:text-[22px] leading-[1.7] text-ink">
                          {l}
                        </p>
                      ))}
                    </div>
                  </div>
                );
              }
              const isFrontMatter = acts[i] === null;
              return isFrontMatter ? (
                <p key={i} className="text-ink2 text-[15px] md:text-base leading-[1.85] my-4 max-w-3xl">{b.text}</p>
              ) : (
                <p key={i} className="font-display italic text-lg text-muted2 text-center my-6">{b.text}</p>
              );
            })}
          </div>

          <div className="border-t border-line pt-10 mt-6 flex flex-wrap gap-4">
            <a data-testid="lecture-download-pdf-bottom" href="/le-crapaud-et-le-berger.pdf" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2.5 border border-ink text-ink hover:bg-ink hover:text-paper transition-colors px-6 py-3 font-mono2 text-[10px] uppercase tracking-[0.2em]">
              <Download size={13} /> {L.download}
            </a>
            <Link data-testid="lecture-order-link-bottom" to="/#contact" className="inline-flex items-center gap-2.5 bg-terra text-paper hover:bg-terraBright transition-colors px-6 py-3 font-mono2 text-[10px] uppercase tracking-[0.2em]">
              {L.order}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LecturePage;
